import BaseListPresenter from "../../base/BaseListPresenter";

class CalendarListPagePresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;

    this.isMounted = false;

    this.filterDebounceTimeout = null;
    this.DEBOUNCE_DELAY = 300;
  }

  async componentDidMount() {
    this.isMounted = true;
    this.init();

    await this.getLocations();
    return this.getObjects();
  }

  componentWillUnmount() {
    this.isMounted = false;
    // Clear any pending timeouts
    if (this.filterDebounceTimeout) {
      clearTimeout(this.filterDebounceTimeout);
    }
  }

  init() {
    const user = this.view.getCurrentUser();
    this.limit = 20;
    this.where = {
      "roles.id": "Recruiter",
    };
    this.search = {};
    this.filter = {};
    this.include = ["employee", "location"];
    this.keys = undefined;
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    if (!this.isMounted) return;

    const collection = this.view.getCollectionName();
    const query = this.createQuery();

    try {
      this.showProgress();

      if (this.findObjectUseCase?.abort) {
        this.findObjectUseCase.abort();
      }

      const objects = await this.findObjectUseCase.execute(collection, query);

      if (!this.isMounted) return;

      this.objects = this.objects.concat(objects);
      this.view.setTotal(this.objects.length);
      this.view.setObjects(this.objects);
      this.hideProgress();
    } catch (error) {
      if (!this.isMounted) return;

      this.hideProgress();

      if (error.name !== "AbortError") {
        this.view.showError(error);
      }
    }
    this.progress = false;
  }

  async getLocations() {
    const collection = "locations";
    try {
      this.view.setState({ loading: true });

      if (this.findObjectUseCase?.abort) {
        this.findObjectUseCase.abort();
      }

      const objects = (await this.findObjectUseCase.execute(collection)) || [];

      if (!this.isMounted) return;

      this.view.setState({
        locations: objects,
        loading: false,
      });

      return objects;
    } catch (error) {
      if (!this.isMounted) return;

      console.error("Error fetching locations:", error);

      if (error.name !== "AbortError") {
        this.view.showError(error);
      }

      this.view.setState({
        locations: [],
        loading: false,
      });

      return [];
    }
  }

  onClickAdd() {
    this.view.navigateTo("/admin/employee/form");
  }

  async onClickView(empDetails) {
    if (!empDetails || !empDetails.id) {
      console.error("No valid coordinator ID found in employee details");
      return;
    }
    console.log("Navigating to calendar for recruiter ID:", empDetails.id);
    this.view.navigateTo(`/hr/recruiter/calendar/${empDetails.id}`);
  }

  debouncedFilter(filterFn) {
    if (this.filterDebounceTimeout) {
      clearTimeout(this.filterDebounceTimeout);
    }

    this.filterDebounceTimeout = setTimeout(() => {
      if (this.isMounted) {
        filterFn();
      }
    }, this.DEBOUNCE_DELAY);
  }

  onChangeLocation(value, schema) {
    this.debouncedFilter(() => {
      this.current = 1;

      this.filter = value
        ? {
            location: {
              name: {
                $regex: value,
                $options: "i",
              },
            },
          }
        : {};

      this.getObjects();
    });
  }

  onChangeUsername(value, schema) {
    this.debouncedFilter(() => {
      this.objects = [];
      this.current = 1;

      this.filter = value
        ? {
            username: {
              $regex: value,
              $options: "i",
            },
          }
        : {};

      this.getObjects();
    });
  }

  onChangeReference(value, schema) {
    this.debouncedFilter(() => {
      this.objects = [];
      this.current = 1;

      this.filter = value
        ? {
            referenceNumber: {
              $regex: value,
              $options: "i",
            },
          }
        : {};

      this.getObjects();
    });
  }

  reset() {
    super.reset();

    this.objects = [];
    this.current = 1;
    this.total = 0;

    if (this.view) {
      this.view.setObjects([]);
      this.view.setTotal(0);
    }
  }
}

export default CalendarListPagePresenter;
